"use client";

import { Loader } from "@nimble/react-requests/components/shared/loader";
import { useRouter } from "nextjs-toploader/app";
import { type ReactNode, useCallback, useEffect, useState } from "react";

import { StateSelectorDialog } from "@/app/(discover)/components/state-selector/state-selector-dialog";
import {
  JobsStateResolution,
  useBestJobsStateUrl,
} from "@/hooks/use-best-jobs-state-url";
import useLastViewedState from "@/hooks/use-last-viewed-state";
import { JobsDestination } from "@/lib/jobs-destination";
import { useJobSearchOrigin } from "@/stores/use-job-search-origin";
import { usePreserveFilters } from "@/stores/use-preserve-filters";

function DialogWrapper() {
  const [isOpen, setIsOpen] = useState(true);
  const { clearPreservedFilters } = usePreserveFilters();

  const closeFn = useCallback(() => {
    clearPreservedFilters();
    setIsOpen(false);
  }, [setIsOpen, clearPreservedFilters]);

  return <StateSelectorDialog isOpen={isOpen} onSelect={closeFn} />;
}

function StateSelectorPageView({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-full items-center justify-center pt-20">
      {children}
    </div>
  );
}

type JobsRouter = Pick<ReturnType<typeof useRouter>, "push">;

function RedirectToJobs({
  url,
  useRouterFn,
}: {
  url: string;
  useRouterFn: () => JobsRouter;
}) {
  const router = useRouterFn();

  useEffect(() => {
    router.push(url);
  }, [url, router]);

  return <Loader />;
}

export default function StateSelectorPageContainer({
  destination,
  useBestJobsStateUrlFn,
  useRouterFn,
}: {
  destination: JobsDestination;
  useBestJobsStateUrlFn: typeof useBestJobsStateUrl;
  useRouterFn: () => JobsRouter;
}) {
  const jobsState = useBestJobsStateUrlFn({
    destination,
    useLastViewedStateFn: useLastViewedState,
    usePreserveFiltersFn: usePreserveFilters,
    useJobSearchOriginFn: useJobSearchOrigin,
  });

  if (jobsState.resolution === JobsStateResolution.Redirect) {
    return (
      <StateSelectorPageView>
        <RedirectToJobs url={jobsState.url} useRouterFn={useRouterFn} />
      </StateSelectorPageView>
    );
  }

  return (
    <StateSelectorPageView>
      <DialogWrapper />
    </StateSelectorPageView>
  );
}
